import { useCallback, useMemo } from "react";
import {
  ConnectionKebabInteractionOption,
  KebabMenuItem,
  useCopyToClipboard,
  useEmitConnectionKebabInteractionEvent,
} from "@zapier/shared";
import {
  Connection,
  useAccount,
  useConnectionActionPermissions,
} from "@zapier/client";
import {
  getAppTemplatesUrl,
  stripVersionFromImplementationName,
} from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";
import { hasRemovableShare } from "../ConnectionShareModal/utils";
import { ConnectionModal } from "../types";
import { useManagedConnectionBlocks } from "../useManagedConnectionBlocks";
import { MenuItemLabel } from "./MenuItemLabel";

interface Input {
  connection: Connection;
  setCurrentConnection: (connection: Connection | undefined) => void;
  setCurrentModal: (modal: any | undefined) => void;
  handleTestConnection: () => void;
}

export function useConnectionMenuItems({
  connection,
  setCurrentConnection,
  setCurrentModal,
  handleTestConnection,
}: Input) {
  const { emit } = useEmitConnectionKebabInteractionEvent();
  const tAction = useTranslations("buttons");
  const tStatus = useTranslations("connections-table-reconnect-cell");
  const tRules = useTranslations("connection-create-rules");
  const copyToClipboard = useCopyToClipboard();

  const {
    canRenameConnection,
    canTestConnection,
    canDeleteConnection,
    canReplaceConnection,
    canTransferConnection,
    canShareConnection,
  } = useConnectionActionPermissions({
    connection,
  });

  const isRestricted = !!connection.isRestricted;
  const {
    isShareBlocked,
    isReconnectBlocked,
    isLoading: isManagedRulesLoading,
  } = useManagedConnectionBlocks(connection);
  const { account } = useAccount();
  // A blocked connection with someone to unshare still opens Share, remove-only.
  const isManagedShareBlocked =
    isShareBlocked && !hasRemovableShare(connection, account?.owner?.id);
  const appName =
    stripVersionFromImplementationName(
      connection?.implementation?.name ?? "",
    ) ||
    (connection?.selectedApi?.split("@")[0] ?? "");
  const restrictedMessage = isRestricted
    ? tStatus("tooltips.restricted", { appName })
    : undefined;

  const disabledShareMessage = restrictedMessage
    ? restrictedMessage
    : !canShareConnection
      ? "You don't have permission to share this connection"
      : isManagedShareBlocked
        ? tRules("managed.share-tooltip")
        : undefined;

  const disabledDeleteMessage = canDeleteConnection
    ? undefined
    : "You don't have permission to delete this connection";

  const disabledRenameMessage = restrictedMessage
    ? restrictedMessage
    : canRenameConnection
      ? undefined
      : "You don't have permission to rename this connection";

  // Replace re-auths the connection, so the managed-app reconnect block applies.
  const disabledReplaceMessage = restrictedMessage
    ? restrictedMessage
    : !canReplaceConnection
      ? "You don't have permission to replace this connection"
      : isReconnectBlocked
        ? tRules("managed.reconnect-tooltip")
        : undefined;

  const disabledTransferMessage = restrictedMessage
    ? restrictedMessage
    : canTransferConnection
      ? undefined
      : "You don't have permission to change the owner of this connection";

  const disabledTestMessage = restrictedMessage ? restrictedMessage : undefined;
  const disabledDiscoverMessage = restrictedMessage
    ? restrictedMessage
    : undefined;
  const disabledCopyIdMessage = restrictedMessage
    ? restrictedMessage
    : undefined;

  const handleShareConnection = useCallback(() => {
    emit(ConnectionKebabInteractionOption.Share);
    setCurrentModal(ConnectionModal.Share);
    setCurrentConnection(connection);
  }, [emit, setCurrentModal, setCurrentConnection, connection]);

  return useMemo(() => {
    return [
      {
        icon: "actionEdit",
        label: "Rename",
        onClick: () => {
          emit(ConnectionKebabInteractionOption.Rename);
          setCurrentConnection(connection);
          setCurrentModal(ConnectionModal.Rename);
        },
        disabled: !canRenameConnection || isRestricted,
        tooltipText: disabledRenameMessage,
      },
      {
        icon: "personAdd",
        label: "Share connection",
        onClick: handleShareConnection,
        disabled:
          !canShareConnection ||
          isRestricted ||
          isManagedShareBlocked ||
          isManagedRulesLoading,
        tooltipText: disabledShareMessage,
      },
      {
        icon: "miscWorld",
        label: "Discover use cases",
        href: getAppTemplatesUrl(connection?.implementation?.slug || ""),
        onClick: () => {
          emit(ConnectionKebabInteractionOption.DiscoverUseCases);
        },
        disabled: isRestricted,
        tooltipText: disabledDiscoverMessage,
      },
      {
        icon: "actionCopy",
        label: "Copy ID",
        onClick: () => {
          if (connection.publicId) {
            emit(ConnectionKebabInteractionOption.CopyId);
            void copyToClipboard(connection.publicId, {
              notificationMessage: "Connection ID copied",
            });
          }
        },
        disabled: !connection.publicId || isRestricted,
        tooltipText: disabledCopyIdMessage,
      },
      {
        label: "divider",
        divider: true,
      },
      {
        icon: "actionCheckConnection",
        label: "Test connection",
        onClick: () => {
          emit(ConnectionKebabInteractionOption.TestConnection);
          setCurrentConnection(connection);
          handleTestConnection();
        },
        disabled: !canTestConnection || isRestricted,
        tooltipText: disabledTestMessage,
      },
      {
        label: "divider",
        divider: true,
      },

      {
        icon: "personKey",
        label: (
          <MenuItemLabel
            label="Replace credential"
            tooltipText="Associate a new credential with your connection. All assets will be updated to use this new credential."
          />
        ),
        onClick: () => {
          emit(ConnectionKebabInteractionOption.Replace);
          setCurrentConnection(connection);
          setCurrentModal(ConnectionModal.Replace);
        },
        disabled:
          !canReplaceConnection ||
          isRestricted ||
          isReconnectBlocked ||
          isManagedRulesLoading,
        tooltipText: disabledReplaceMessage,
      },
      {
        icon: "personSettings",
        label: tAction("change-owner"),
        onClick: () => {
          emit(ConnectionKebabInteractionOption.ChangeOwner);
          setCurrentConnection(connection);
          setCurrentModal(ConnectionModal.ChangeOwner);
        },
        disabled: !canTransferConnection || isRestricted,
        tooltipText: disabledTransferMessage,
      },
      {
        label: "divider",
        divider: true,
      },
      {
        icon: "actionTrash",
        label: tAction("delete"),
        onClick: () => {
          emit(ConnectionKebabInteractionOption.Delete);
          setCurrentConnection(connection);
          setCurrentModal(ConnectionModal.Delete);
        },
        disabled: !canDeleteConnection,
        tooltipText: disabledDeleteMessage,
        hasWarning: true,
      },
    ].filter(Boolean) as KebabMenuItem[];
  }, [
    connection,
    setCurrentConnection,
    setCurrentModal,
    canRenameConnection,
    canTestConnection,
    canReplaceConnection,
    canTransferConnection,
    canDeleteConnection,
    canShareConnection,
    isRestricted,
    isManagedShareBlocked,
    isReconnectBlocked,
    isManagedRulesLoading,
    disabledShareMessage,
    disabledDeleteMessage,
    disabledRenameMessage,
    disabledReplaceMessage,
    disabledTransferMessage,
    disabledTestMessage,
    disabledDiscoverMessage,
    disabledCopyIdMessage,
    emit,
    handleTestConnection,
    handleShareConnection,
    copyToClipboard,
    tAction,
  ]);
}
